<template>
    <div>
        <div class="menu" :class="{'minimized': sidePanel.minimized, 'details': sidePanel.details}">
            <ul :class="{'hide-details': !sidePanel.details}">
                <template v-for="(item, index) in items">
                    <li class="sub-header" v-if="item.header" :key="'h' + index">{{ item.header }}</li>
                    <router-link v-else :key="'i' + index" :to="item.to" custom v-slot="{ navigate }">
                        <li @click="navigate" :class="{'active': item.active}">
                            <b-tooltip :label="item.label" position="is-right" append-to-body type="is-light" size="is-large">
                                <i class="fad fa-fw" :class="item.icon"></i>
                            </b-tooltip><span class="nav-title">{{ item.label }}</span>
                            <div class="meta" v-if="item.meta">{{ item.meta }}</div>
                            <div class="meta mt-3" v-if="item.flag">
                                <div><i class="fas mr-2 is-size-6 activity-flag" :class="[item.flag.icon || 'fa-exclamation-triangle', item.flag.cls]"></i>{{ item.flag.text }}</div>
                            </div>
                        </li>
                    </router-link>
                </template>
            </ul>
        </div>
        <div class="controls">
            <div class="columns is-gapless">
                <div v-if="!sidePanel.minimized" class="column is-size-7 has-text-grey is-hidden-mobile" style="padding-top: 7px !important;">v{{ version }}</div>
                <div class="column has-text-right">
                    <span @click="toggleDetails()" v-if="!sidePanel.minimized"><i class="fad mt-2 mr-3" :class="{'fa-bars': sidePanel.details, 'fa-diagram-cells': !sidePanel.details}"></i></span>
                    <span @click="minimize(false)" v-if="sidePanel.minimized"><i class="fad fa-chevrons-right mt-2"></i></span>
                    <span @click="minimize(true)" v-else><i class="fad fa-chevrons-left mt-2"></i></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import pkg from '../../../package.json';
    import {routeSection, NARROW_BREAKPOINT} from "../../constants/navigation";
    import navFlags from "./navFlags";
    import {sitePage} from "../../constants/siteMap";

    const ATTENTION = {text: 'Requires Attention', cls: 'has-text-warning'};

    // Your Account sections (Account.vue reads ?section= from the route)
    const ACCOUNT_SECTIONS = [
        {key: 'account', ...sitePage('accountSettings')},
        {key: 'login', ...sitePage('loginCredentials')},
        {key: 'billing', ...sitePage('billingInformation')},
        {key: 'notifications', ...sitePage('notifications')},
        {key: 'schedule', ...sitePage('workSchedule')},
        {key: 'certs', ...sitePage('certificationsOrganizations')},
    ];

    export default {
        name: "SidePanelNav",
        mixins: [navFlags],
        data: function() {
            return {
                version: pkg.version
            };
        },
        computed: {
            ...mapGetters([
                'currentUser', 'sidePanel', 'hasExpiredCourses', 'expiredCourses', 'hasCartEnrollment',
                // dashboard shortcuts
                'activeEnrollmentCount',
                // Certificates & Forms "N Ready to Apply" tag
                'readyApplicationCount', 'availableApplicationsLoaded',
            ]),
            section() {
                return routeSection(this.$route);
            },
            items() {
                const name = this.$route.name;
                const on = (...names) => names.includes(name);
                // One page's routed views (/enrollments, /enrollments/completed ...)
                // become sibling items. The default view links by path: vue-router
                // copies the current params on a same-name push, so `{name}` alone
                // would keep the current view.
                const view = (routeName, path, v, def) => ({
                    ...def,
                    to: v ? {name: routeName, params: {view: v}} : {path},
                    active: on(routeName) && (this.$route.params.view || '') === (v || '')
                });

                switch (this.section && this.section.key) {
                    case 'dashboard':
                        return this.dashboardItems();
                    case 'courses': {
                        const items = [
                            {header: 'Your Courses'},
                            view('enrollments', '/enrollments', null, {
                                ...sitePage('currentEnrollments'),
                            }),
                            view('enrollments', '/enrollments', 'completed', {
                                ...sitePage('completedEnrollments'),
                                flag: this.incompleteCourseApps ? ATTENTION : null
                            }),
                        ];
                        if (this.hasExpiredCourses) {
                            items.push({
                                ...sitePage('reactivateCourses'), to: {name: 'reactivation-checkout'}, active: on('reactivation-checkout'),
                                flag: {text: this.expiredCourses.count + ' Expired', cls: 'has-text-warning'}
                            });
                        }
                        items.push(
                            {header: 'Enroll'},
                            {
                                ...sitePage('courseCatalog'), to: {name: 'course-catalog'}, active: on('course-catalog', 'course-enroll'),
                            },
                            {
                                ...sitePage('buildBundle'), to: {name: 'package-builder'}, active: on('package-builder'),
                            }
                        );
                        if (this.hasCartEnrollment) {
                            items.push({
                                ...sitePage('cartCheckout'), to: {name: 'cart-checkout'}, active: on('cart-checkout'),
                                flag: {text: 'Action Required', cls: 'has-text-warning'}
                            });
                        }
                        return items;
                    }
                    case 'conferences':
                        return [
                            {header: 'Your Conferences'},
                            {
                                ...sitePage('conferenceRecords'), to: {name: 'registrations'}, active: on('registrations'),
                                flag: this.incompleteConfApps ? ATTENTION : null
                            },
                            {header: 'Register'},
                            {
                                ...sitePage('conferenceCenter'), to: {name: 'conference-center'}, active: on('conference-center', 'register'),
                            },
                        ];
                    case 'pd':
                        return [
                            {header: 'Professional Development'},
                            view('pd', '/professional-development', null, {
                                ...sitePage('pdOverview'),
                            }),
                            view('pd', '/professional-development', 'salary', {
                                ...sitePage('pdSalary'),
                            }),
                            view('pd', '/professional-development', 'renewal', {
                                ...sitePage('pdRenewal'),
                            }),
                            view('pd', '/professional-development', 'settings', {
                                ...sitePage('pdSettings'),
                            }),
                            {header: 'Earn Hours'},
                            {
                                ...sitePage('buildBundle'), to: {name: 'package-builder'},
                                meta: 'Buy clock hours at bundle pricing and spend them across courses.'
                            },
                            {
                                ...sitePage('courseCatalog'), to: {name: 'course-catalog'},
                            },
                            {
                                ...sitePage('workWithRep'), to: {name: 'package-builder', query: {phase: 'lead'}},
                            },
                        ];
                    case 'records': {
                        const items = [{header: 'Certificates & Forms'}];
                        // Only worth a menu item while something is actually
                        // started (bare /reports lands on certificates otherwise)
                        if (this.inProgressApplicationCount > 0 || (on('reports') && this.$route.params.view === 'applications')) {
                            items.push(view('reports', '/reports', 'applications', {
                                ...sitePage('inProgressApplications'),
                            }));
                        }
                        items.push(
                            view('reports', '/reports', 'completed', {
                                ...sitePage('completedCertificates'),
                            }),
                            // Below Completed: Completed is the default landing view
                            view('reports', '/reports', 'available', {
                                ...sitePage('availableApplications'),
                                flag: this.readyApplicationCount ? {text: this.readyApplicationCount + ' Ready to Apply', cls: 'has-text-success', icon: 'fa-circle-check'} : null
                            }),
                            view('reports', '/reports', 'receipts', {
                                ...sitePage('receipts'),
                            }),
                            {header: 'Reports'},
                            {
                                ...sitePage('studyActivity'), to: {name: 'study-activity'}, active: on('study-activity'),
                                flag: this.activityFlag
                            },
                        );
                        return items;
                    }
                    case 'account': {
                        const current = this.$route.params.section || 'account';
                        return [
                            {header: 'Settings'},
                            ...ACCOUNT_SECTIONS.map(s => ({
                                label: s.label, icon: s.icon, meta: s.meta,
                                // default section lives at the bare /account path
                                to: s.key === 'account' ? {path: '/account'} : {name: 'account', params: {section: s.key}},
                                active: on('account') && current === s.key
                            }))
                        ];
                    }
                    case 'support':
                        return [
                            {header: 'Support'},
                            {
                                ...view('support', '/support', null, {
                                    ...sitePage('helpCenter'),
                                }),
                                // topic and article pages belong to the Help Center
                                active: on('support') && ['', 'topic', 'help'].includes(this.$route.params.view || '')
                            },
                            view('support', '/support', 'tickets', {
                                ...sitePage('yourTickets'),
                            }),
                            view('support', '/support', 'new', {
                                ...sitePage('newTicket'),
                            }),
                        ];
                    default:
                        return [];
                }
            }
        },
        methods: {
            // The dashboard's panel is shortcuts only (no to-dos, no course
            // list): links into the sections, with the personal ones surfacing
            // only when the student has something there.
            dashboardItems() {
                const items = [{header: 'Shortcuts'}];
                const active = this.activeEnrollmentCount || 0;
                if (active > 0) {
                    items.push({
                        label: 'Current Enrollments', icon: 'fa-circle-play', to: {path: '/enrollments'},
                        meta: active === 1 ? 'Pick up the course you have in progress.' : 'Pick up any of the ' + active + ' courses you have in progress.'
                    });
                }
                if ((this.registrations || []).length > 0) {
                    items.push({
                        label: 'Conference Records', icon: 'fa-screen-users', to: {name: 'registrations'},
                        meta: 'Your conference registrations and attendance records.',
                        flag: this.incompleteConfApps ? ATTENTION : null
                    });
                }
                items.push(
                    {label: 'Browse the Course Catalog', icon: 'fa-browsers', meta: 'Find and enroll in online courses.', to: {name: 'course-catalog'}},
                    {label: 'Register for a Conference', icon: 'fa-presentation', meta: 'Conference Center registrations and credit.', to: {name: 'conference-center'}},
                    {label: 'Get Help', icon: 'fa-messages-question', meta: 'Search common questions or contact our team.', to: {name: 'support'}}
                );
                return items;
            },
            toggleDetails: function() {
                this.$store.commit('setDetails', !this.sidePanel.details);
            },
            minimize: function(state) {
                this.$store.commit('setMinimized', state);
            },
            autoSize: function() {
                let width = window.innerWidth;
                if (width < NARROW_BREAKPOINT) {
                    this.$store.commit('setMinimized', true);
                } else {
                    this.$store.commit('setMinimized', false);
                }
            }
        },
        watch: {
            // Only the Certificates & Forms menu shows the "Ready to Apply" tag
            'section.key': {
                immediate: true,
                handler(key) {
                    if (key === 'records' && !this.availableApplicationsLoaded) this.$store.dispatch('getAvailableApplications').catch(() => {});
                }
            }
        },
        mounted() {
            window.addEventListener('resize', this.autoSize);
            this.autoSize();
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.autoSize);
        }
    };
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/sidemenu";
</style>
